import { useDateFormat } from 'react-common'
import { useTranslation } from 'react-i18next'
import type { LearnerObjectiveAccessFullModel } from '@/api/models/aggregate'
import { useProctoringSessionFinished } from '../CourseInfo/hooks/useProctoringSessionFinished'

export type NotAvailableResult = {
  isProctorSessionLoading?: boolean
  isAddableToMyCourses?: boolean
  canReview: boolean
  isNotAvailable: boolean
  isNotAvailableMessage: string
}

export const useIsNotAvailable = (aggregate?: LearnerObjectiveAccessFullModel | null): NotAvailableResult => {
  const { t } = useTranslation()
  const { formatDateTime } = useDateFormat()

  const [isCourseReviewDisabled, isProctorSessionLoading] = useProctoringSessionFinished()

  if (isProctorSessionLoading) {
    return { isProctorSessionLoading, isNotAvailable: false, isNotAvailableMessage: '', canReview: true }
  }

  if (!aggregate || isCourseReviewDisabled) {
    return { isNotAvailable: true, isNotAvailableMessage: t('courseIsInGradingWarning'), canReview: false }
  }

  const { availabilityDate, availabilityEndDate, objectiveWorkflowAggregate } = aggregate
  const canBeRetaken = aggregate.retake
  const now = Date.now()

  if (availabilityDate) {
    const availability = new Date(availabilityDate)

    if (now < availability.getTime())
      return {
        isNotAvailable: true,
        isNotAvailableMessage: t('courseDetails.startButton.tooltip.willBeAvailable', {
          date: formatDateTime(availability),
        }),
        isAddableToMyCourses: !objectiveWorkflowAggregate?.isSelfEnrolled,
        canReview: false,
      }
  }

  if (availabilityEndDate) {
    const availabilityEnd = new Date(availabilityEndDate)

    if (now > availabilityEnd.getTime() && (!objectiveWorkflowAggregate?.lastObjectiveWorkflow || canBeRetaken)) {
      return {
        isNotAvailable: true,
        isNotAvailableMessage: t('courseDetails.startButton.tooltip.wasAvailable', {
          date: formatDateTime(availabilityEnd),
        }),
        isAddableToMyCourses: !objectiveWorkflowAggregate?.isSelfEnrolled,
        canReview: true,
      }
    }
  }

  return { isNotAvailable: false, isNotAvailableMessage: '', canReview: true }
}
